import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { OverallCard } from "@/components/overall/OverallCard";
import { Plus, Sparkles } from "lucide-react";

export default function Overalls() {
  const { currentId } = useCompetition();
  const [cats, setCats] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [overalls, setOveralls] = useState([]);
  const [results, setResults] = useState({});
  const [nome, setNome] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!currentId) return;
    const [c, a, o] = await Promise.all([
      api.get(`/competitions/${currentId}/categories`),
      api.get(`/competitions/${currentId}/athletes`),
      api.get(`/competitions/${currentId}/overalls`),
    ]);
    setCats(c.data);
    setAthletes(a.data);
    setOveralls(o.data);
    const entries = await Promise.all(
      o.data.map((ov) => api.get(`/overalls/${ov.id}/result`).then((r) => [ov.id, r.data]).catch(() => [ov.id, null]))
    );
    setResults(Object.fromEntries(entries));
  }, [currentId]);

  useEffect(() => { load(); }, [load]);

  const autoGenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/competitions/${currentId}/overalls/auto-generate`);
      setMsg(`${r.data.created} Overall(s) criado(s), ${r.data.updated} atualizado(s) — ${r.data.total} modalidade(s) encontrada(s).`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return;
    await api.post(`/competitions/${currentId}/overalls`, { nome: nome.trim(), category_ids: [] });
    setNome("");
    setShowForm(false);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este Overall? As notas lançadas nele serão perdidas.")) return;
    await api.delete(`/overalls/${id}`);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Comparação Máxima</div>
          <h1 className="font-display text-5xl font-black tracking-tight">OVERALL</h1>
        </div>
        <CompetitionSelector />
      </div>

      <div className="card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="overall-toolbar">
        <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={autoGenerate} disabled={busy || !currentId} data-testid="overall-auto-generate">
          <Sparkles size={15} /> {busy ? "Agrupando..." : "Agrupar campeões por modalidade"}
        </button>
        <button type="button" className="btn-ghost inline-flex items-center gap-2" onClick={() => setShowForm((s) => !s)} data-testid="overall-new-toggle">
          <Plus size={15} /> Overall manual
        </button>
        <span className="text-xs text-[var(--text-secondary)] flex-1 min-w-[240px]">
          Cria um Overall por modalidade (Bodybuilding, Men's Physique, Wellness, Bikini...) com os campeões de cada classe. Depois ajuste livremente cada Overall.
        </span>
        {msg && <span className="text-xs text-[#C9A84C] w-full" data-testid="overall-auto-msg">{msg}</span>}
        {showForm && (
          <form onSubmit={submit} className="flex gap-2 w-full" data-testid="overall-new-form">
            <input data-testid="overall-nome" className="input-field flex-1" placeholder="Ex: OVERALL MASTERS" value={nome} onChange={(e) => setNome(e.target.value.toUpperCase())} required />
            <button data-testid="overall-submit" className="btn-primary inline-flex items-center gap-2" disabled={!nome.trim()}><Plus size={16} /> Criar</button>
          </form>
        )}
      </div>

      <div className="space-y-4">
        {overalls.length === 0 && (
          <div className="card p-8 text-center text-[var(--text-secondary)]" data-testid="overall-empty">
            Nenhum Overall ainda. Use “Agrupar campeões por modalidade” para gerar automaticamente.
          </div>
        )}
        {overalls.map((ov) => (
          <OverallCard key={ov.id} ov={ov} result={results[ov.id]} cats={cats} athletes={athletes} onChanged={load} onDelete={() => del(ov.id)} />
        ))}
      </div>
    </div>
  );
}
